import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ShopifyService } from '../../services/shopify.service';
import { CartService } from '../../services/cart.service';
import { ShopifyProduct } from '../models';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-gulli-sticker',
    templateUrl: './gulli-sticker.component.html',
    styleUrls: ['./gulli-sticker.component.scss'],
    imports: [TranslateModule],
})
export class GulliStickerComponent implements OnInit, OnDestroy {
  stickerProduct: ShopifyProduct | null = null;
  isLoading: boolean = true;
  showAddedToCartNotification: boolean = false;

  private subscription: Subscription | null = null;
  private notificationTimeout: any;

  readonly PLACEHOLDER_IMAGE = '../../assets/images/gulli-sticker-placeholder.webp';

  constructor(
    private shopifyService: ShopifyService,
    private cartService: CartService
  ) {}

  ngOnInit(): void {
    this.loadStickerProduct();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }
  }

  private loadStickerProduct(): void {
    this.isLoading = true;

    this.subscription = this.shopifyService.getProductByHandle('gulli-sticker').subscribe({
      next: (response) => {
        const product = response?.data?.productByHandle;
        if (product && product.availableForSale && product.variants.edges.length > 0) {
          const variant = product.variants.edges[0].node;
          this.stickerProduct = {
            id: product.id,
            title: product.title,
            description: product.description || '',
            handle: product.handle,
            imageUrl: product.images.edges.length > 0
              ? product.images.edges[0].node.url
              : this.PLACEHOLDER_IMAGE,
            price: {
              amount: variant.price.amount,
              currencyCode: variant.price.currencyCode
            },
            compareAtPrice: variant.compareAtPrice ? {
              amount: variant.compareAtPrice.amount,
              currencyCode: variant.compareAtPrice.currencyCode
            } : undefined,
            variantId: variant.id,
            availableForSale: variant.availableForSale
          };
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  addToCart(): void {
    if (!this.stickerProduct) return;
    this.cartService.addShopifyProduct(this.stickerProduct, 1);

    this.showAddedToCartNotification = true;
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }
    this.notificationTimeout = setTimeout(() => {
      this.showAddedToCartNotification = false;
    }, 3000);
  }

  formatPrice(amount: string, currencyCode: string): string {
    const price = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(price);
  }

  hasDiscount(): boolean {
    if (!this.stickerProduct?.compareAtPrice) return false;
    return parseFloat(this.stickerProduct.compareAtPrice.amount) > parseFloat(this.stickerProduct.price.amount);
  }

  getDiscountPercentage(): number {
    if (!this.stickerProduct?.compareAtPrice) return 0;
    const original = parseFloat(this.stickerProduct.compareAtPrice.amount);
    const current = parseFloat(this.stickerProduct.price.amount);
    return Math.round(((original - current) / original) * 100);
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = this.PLACEHOLDER_IMAGE;
  }
}
